const getTelegramWebApp = () => {
    if (typeof window === "undefined" || !window.Telegram || !window.Telegram.WebApp) {
        return null;
    }

    return window.Telegram.WebApp;
};

// Возвращает значение launch-параметра из query/hash URL.
const getLaunchParam = (name) => {
    if (typeof window === "undefined") {
        return "";
    }

    const searchValue = new URLSearchParams(window.location.search || "").get(name);
    if (searchValue) {
        return searchValue;
    }

    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) {
        return "";
    }

    return new URLSearchParams(hash).get(name) || "";
};

// Извлекает и декодирует initData из Telegram launch-параметров.
const getTelegramInitDataFromLaunchParams = () => {
    const raw = getLaunchParam("tgWebAppData");
    if (!raw) {
        return "";
    }

    try {
        return decodeURIComponent(raw);
    } catch (error) {
        return raw;
    }
};

// Парсит initData в initDataUnsafe-объект для fallback-режима.
const parseInitDataUnsafe = (initData) => {
    if (!initData) {
        return {};
    }

    try {
        const params = new URLSearchParams(initData);
        const userRaw = params.get("user");
        const user = userRaw ? JSON.parse(userRaw) : undefined;
        return {
            user,
            query_id: params.get("query_id") || "",
            start_param: params.get("start_param") || "",
            auth_date: params.get("auth_date") || "",
            hash: params.get("hash") || "",
        };
    } catch (error) {
        return {};
    }
};

export const isTelegramWebAppAvailable = () => Boolean(getTelegramWebApp());

// Нормализует объект inset'ов в числовой формат.
const normalizeInsets = (insets) => {
    if (!insets) {
        return {top: 0, bottom: 0, left: 0, right: 0};
    }

    return {
        top: Number(insets.top) || 0,
        bottom: Number(insets.bottom) || 0,
        left: Number(insets.left) || 0,
        right: Number(insets.right) || 0,
    };
};

// Проставляет CSS-переменные safe-area, чтобы верстка могла учитывать Telegram WebView.
const syncTelegramInsetCssVars = (webApp) => {
    if (typeof document === "undefined") {
        return;
    }

    const safeInsets = normalizeInsets(webApp?.safeAreaInset);
    const contentInsets = normalizeInsets(webApp?.contentSafeAreaInset);
    const platform = String(webApp?.platform || "").toLowerCase();
    const isApplePlatform = platform === "ios" || platform === "macos";
    // В web/desktop клиентах Telegram горизонтальные inset'ы могут быть асимметричными и ломать отступы карточек.
    const isDesktopLikePlatform = platform === "tdesktop" || platform.startsWith("web");
    const horizontalSafeInsets = isDesktopLikePlatform
        ? {left: 0, right: 0}
        : {
            left: Math.max(safeInsets.left, contentInsets.left),
            right: Math.max(safeInsets.right, contentInsets.right),
        };
    const iosTopInset = isApplePlatform ? Math.max(safeInsets.top, contentInsets.top) : 0;
    const iosBottomInset = isApplePlatform ? Math.max(safeInsets.bottom, contentInsets.bottom) : 0;
    const rootStyle = document.documentElement.style;

    rootStyle.setProperty("--tg-safe-area-top", `${safeInsets.top}px`);
    rootStyle.setProperty("--tg-safe-area-bottom", `${safeInsets.bottom}px`);
    rootStyle.setProperty("--tg-safe-area-left", `${horizontalSafeInsets.left}px`);
    rootStyle.setProperty("--tg-safe-area-right", `${horizontalSafeInsets.right}px`);
    rootStyle.setProperty("--tg-content-safe-area-top", `${contentInsets.top}px`);
    rootStyle.setProperty("--tg-content-safe-area-bottom", `${contentInsets.bottom}px`);
    rootStyle.setProperty("--tg-content-safe-area-left", `${horizontalSafeInsets.left}px`);
    rootStyle.setProperty("--tg-content-safe-area-right", `${horizontalSafeInsets.right}px`);
    rootStyle.setProperty("--tg-ios-safe-top", `${iosTopInset}px`);
    rootStyle.setProperty("--tg-ios-safe-bottom", `${iosBottomInset}px`);
    // Дополнительный верхний буфер включаем только на iOS/macOS (Telegram WebView).
    rootStyle.setProperty("--tg-extra-top-gap", isApplePlatform ? "32px" : "0px");
    rootStyle.setProperty("--tg-extra-top-gap-home", isApplePlatform ? "16px" : "0px");
};

// Инициализируем Telegram WebApp только если окружение доступно.
export const initTelegramWebApp = () => {
    const webApp = getTelegramWebApp();
    if (!webApp) {
        return false;
    }

    webApp.ready();
    webApp.expand();
    syncTelegramInsetCssVars(webApp);

    if (typeof webApp.onEvent === "function") {
        // Поддерживаем актуальные inset'ы при повороте/изменении контейнера.
        const syncInsets = () => syncTelegramInsetCssVars(webApp);
        webApp.onEvent("safeAreaChanged", syncInsets);
        webApp.onEvent("contentSafeAreaChanged", syncInsets);
    }

    return true;
};

export const getTelegramPlatform = () => {
    const webApp = getTelegramWebApp();
    return webApp?.platform || "";
};

// Возвращаем initData из Telegram WebApp.
export const getTelegramInitData = () => {
    const webApp = getTelegramWebApp();
    if (webApp?.initData) {
        return webApp.initData;
    }

    return getTelegramInitDataFromLaunchParams();
};

export const getTelegramInitDataUnsafe = () => {
    const webApp = getTelegramWebApp();
    if (webApp?.initDataUnsafe && Object.keys(webApp.initDataUnsafe).length > 0) {
        return webApp.initDataUnsafe;
    }

    return parseInitDataUnsafe(getTelegramInitData());
};

export const getTelegramUser = () => {
    const initDataUnsafe = getTelegramInitDataUnsafe();
    return initDataUnsafe?.user || null;
};

export const getTelegramUserId = () => {
    const user = getTelegramUser();
    return user?.id || null;
};

export const getTelegramSafeAreaInsets = () => {
    const webApp = getTelegramWebApp();
    if (!webApp) {
        return undefined;
    }

    const safeInsets = normalizeInsets(webApp.safeAreaInset);
    const contentInsets = normalizeInsets(webApp.contentSafeAreaInset);
    const platform = String(webApp.platform || "").toLowerCase();
    const isApplePlatform = platform === "ios" || platform === "macos";

    if (!isApplePlatform) {
        return safeInsets;
    }

    // На iOS/macOS берём максимум между safe/content, чтобы исключить перекрытие Telegram-контролами.
    return {
        top: Math.max(safeInsets.top, contentInsets.top),
        bottom: Math.max(safeInsets.bottom, contentInsets.bottom),
        left: Math.max(safeInsets.left, contentInsets.left),
        right: Math.max(safeInsets.right, contentInsets.right),
    };
};

export const showTelegramBackButton = () => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!backButton) {
        return;
    }

    backButton.show();
};

export const hideTelegramBackButton = () => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!backButton) {
        return;
    }

    backButton.hide();
};

export const setTelegramBackButtonHandler = (handler) => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!backButton || typeof handler !== "function") {
        return () => {};
    }

    backButton.onClick(handler);

    return () => {
        backButton.offClick(handler);
    };
};

export const triggerTelegramHapticNotification = (type) => {
    const hapticFeedback = getTelegramWebApp()?.HapticFeedback;
    if (!hapticFeedback?.notificationOccurred) {
        return;
    }

    hapticFeedback.notificationOccurred(type);
};

export const openTelegramShare = ({text = "", url = ""}) => {
    const webApp = getTelegramWebApp();
    if (!webApp?.openTelegramLink) {
        return false;
    }

    const resolvedUrl = url || "https://t.me";
    const shareUrl = "https://t.me/share/url?url="
        + encodeURIComponent(resolvedUrl)
        + "&text="
        + encodeURIComponent(text);
    webApp.openTelegramLink(shareUrl);
    return true;
};

export const requestTelegramWriteAccess = () => {
    const webApp = getTelegramWebApp();
    if (!webApp?.requestWriteAccess) {
        return Promise.resolve(true);
    }

    return new Promise((resolve) => {
        webApp.requestWriteAccess((isAllowed) => {
            resolve(Boolean(isAllowed));
        });
    });
};
